import MojitoApplications from 'mojito/applications';
import MojitoServices from 'mojito/services';
import MojitoModules from 'mojito/modules';

import {mergeTo, mergeToHierarchy} from '#core/utils/config-utils.js';
import {PROXY_SHOW_DEPOSIT} from '#core/utils/intent-types.js';
import {getDeeplink} from '#core/utils/url-utils.js';

import {AbstractVisualFeature} from '#core/application/modules/visual-feature.js';
import {Bonuses} from '#core/application/modules/bonuses/bonuses.js';

import {BettingCenter} from '../betting-center.js';

import BETSLIP_NOTIFICATIONS_MAP from './betslip-notifications-map.json';
import {betslipFactory} from './betslip.factory';

import SCHEMA from './betslip.schema.yaml';
import PALETTE_SCHEMA from './betslip.palette-schema.yaml';

const {SHOW_BETSLIP_CONDITION} = MojitoApplications.Sports.ApplicationTypes;
const {COMMIT_BETSLIP_MODE} = MojitoServices.Betslip.types;
const {BETS_TYPE} = MojitoModules.YourBets.types;
const {BETSLIP_TYPE} = MojitoModules.SlidingBetslip.types;

class BetslipFeature extends AbstractVisualFeature {
    get name() {
        return 'Betslip';
    }

    get configSchema() {
        return SCHEMA;
    }

    get paletteSchema() {
        return PALETTE_SCHEMA;
    }

    get isFloating() {
        return Boolean(this.config.floating);
    }

    getSerializableVisualConfig(PALETTE) {
        const config = this.config;
        const {numericKeypad, showTaxAbsorptionMessage, settings, selectionsCount, openBetsCount} = config;
        const {automaticPriceChangeAcceptance} = settings;

        return mergeTo(betslipFactory(PALETTE), {
            BetslipOptions: {
                floating: {
                    enabled: this.isFloating,
                },
            },
            BetBuilder: {
                showBetslipSettings: settings.showSettings,
                showTeasers: config.showTeasers,
                showBankers: config.showBankers,
                showGroupedSingles: config.showGroupedSingles ?? !GENBET_BASED,
                showTabs: config.showTabs,
            },
            YourBets: {
                availableBetTabs: GENBET_BASED
                    ? [BETS_TYPE.OPEN, BETS_TYPE.LIVE, BETS_TYPE.SETTLED]
                    : [BETS_TYPE.OPEN, BETS_TYPE.LIVE],
            },
            Betslip: {
                showBetslipSettingsInEmptyBetslip: settings.showSettings && settings.showSettingsInEmptyBetslip,
            },
            BettingSettings: {
                _mobile_enableQuickBetslipConfiguration: settings.enableQuickBetslipConfiguration,
                automaticPriceChangeAcceptance: {
                    configurable: {
                        all: automaticPriceChangeAcceptance.all,
                        higher: automaticPriceChangeAcceptance.higher,
                        lower: automaticPriceChangeAcceptance.lower,
                    },
                },
            },
            BetslipNotifications: {
                showDepositOnInsufficientFunds: config.showDepositOnInsufficientFunds,
                notificationsMap: BETSLIP_NOTIFICATIONS_MAP,
                depositIntent: PROXY_SHOW_DEPOSIT,
            },
            NumericKeypad: {
                incrementalNumericKeypadWide: {
                    increaseButtons: {
                        values: {
                            key1: numericKeypad.key1,
                            key2: numericKeypad.key2,
                            key3: numericKeypad.key3,
                            key4: numericKeypad.key4,
                        },
                    },
                },
                incrementalNumericKeypadNarrow: {
                    increaseButtons: {
                        values: {
                            key1: numericKeypad.key1,
                            key2: numericKeypad.key2,
                            key3: numericKeypad.key3,
                            key4: numericKeypad.key4,
                        },
                    },
                },
            },
            CompactNumericKeypad: {
                incrementalView: {
                    increaseButtons: {
                        values: {
                            key1: numericKeypad.key1,
                            key2: numericKeypad.key2,
                            key3: numericKeypad.key3,
                            key4: numericKeypad.key4,
                        },
                    },
                },
            },
            TaxAbsorptionDisclaimer: {
                hide: !showTaxAbsorptionMessage,
            },
            BettingCenterView: {
                selectionsCount: {
                    hidden: !selectionsCount.showSelectionsCount,
                    displayWhenEmpty: selectionsCount.showSelectionsCountWhenEmpty,
                },
                openBetsCount: {
                    hidden: !openBetsCount.showOpenBetsCount,
                    displayWhenEmpty: openBetsCount.showOpenBetsCountWhenEmpty,
                },
            },
        });
    }

    updateMojitoConfig(mojitoConfig) {
        const config = this.config;

        let BETSLIP_AUTO_SHOW_UP_TYPE = BETSLIP_TYPE.QUICK_BETSLIP;

        if (config.disableBetslipAutoShowUp) {
            BETSLIP_AUTO_SHOW_UP_TYPE = null;
        } else if (config.disableQuickBetslip) {
            BETSLIP_AUTO_SHOW_UP_TYPE = BETSLIP_TYPE.STANDARD_BETSLIP;
        }

        mergeToHierarchy(mojitoConfig, 'services.betslipService', {
            service: MojitoServices.Betslip.service,
            // Disabled for GenBet integration as it is not supported there.
            refreshBonusOffersInterval: GENBET_BASED ? 0 : Bonuses.refreshBonusOffersInterval * 1000,
            refreshOveraskStatusInterval: config.refreshOveraskStatusInterval * 1000,
            refreshOveraskStatusDelay: config.refreshOveraskStatusDelay * 1000,
            enableBonusCall: Bonuses.doSeparateBonusCall,
        });

        mergeToHierarchy(mojitoConfig, 'services.stores.betslip', {
            storageTimeInHours: config.storageTimeInHours,
            allowBetPlacement: config.allowBetPlacement,

            // Disabled for GenBet integration as it is not supported there.
            enableBonusValidation: !GENBET_BASED && Bonuses.doBonusValidation,

            commitBetslipMode: COMMIT_BETSLIP_MODE.PLACE_BET,
        });

        mergeToHierarchy(mojitoConfig, 'application.stores.application', {
            betslipAutoShowUp: {
                [SHOW_BETSLIP_CONDITION.ADD_FIRST_SELECTION]: BETSLIP_AUTO_SHOW_UP_TYPE,
                [SHOW_BETSLIP_CONDITION.CONTAINS_MAX_ACCUMULATOR]: BETSLIP_AUTO_SHOW_UP_TYPE,
                ...(GENBET_BASED
                    ? {[SHOW_BETSLIP_CONDITION.CONTAINS_ONE_BET_BUILDER]: BETSLIP_AUTO_SHOW_UP_TYPE}
                    : {[SHOW_BETSLIP_CONDITION.ADD_MATCH_ACCA]: BETSLIP_AUTO_SHOW_UP_TYPE}),
            },
        });

        if (!config.enableShortenedShareUrl) {
            // todo rework when MOJ-17643 is delivered
            MojitoServices.Betslip.service.getShareUrl = function (path, betRefs, affiliate) {
                return Promise.resolve({
                    url: getDeeplink({
                        path,
                        betRefs,
                        append: {clear_slip: 'Y', source: 'SHARE_BET', affiliate},
                    }),
                });
            };
        }

        super.updateMojitoConfig(mojitoConfig);
    }
}

export const Betslip = new BetslipFeature(BettingCenter);
